import { FlexRow } from '@minecraft.atoms';
import React, { FC } from 'react';
import { gqlSocialMedia, gqlSocialMediaOverview } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { formatMetricCount } from 'minecraft/src/components/utils/helpers/helpers';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { SocialMediaButton } from '../SocialMediaButton';

export type SocialMediaSectionProps = {
  socialMedia: gqlSocialMedia;
  id: string;
  showInstagram?: boolean;
  showYoutube?: boolean;
};

type SocialPlatformButtonProps = {
  platform: 'instagram' | 'youtube';
  overview?: gqlSocialMediaOverview;
  id: string;
};

const SocialPlatformButton: FC<SocialPlatformButtonProps> = ({ platform, overview, id }) => {
  const isLinked = Boolean(overview);
  const { t } = useTranslation();

  const buttonLabel = isLinked ? formatMetricCount(overview?.metrics?.followerCount) : 'N/A';

  const buttonAriaLabel = isLinked
    ? t('common:socialLinking.button.linked.ariaLabel', {
        socialMediaPlatform: platform === 'instagram' ? 'Instagram' : 'YouTube',
      })
    : t('common:socialLinking.button.notLinked.ariaLabel', {
        socialMediaPlatform: platform === 'instagram' ? 'Instagram' : 'YouTube',
      });

  const iconName = platform === 'instagram' ? 'profile-social-instagram' : 'profile-social-youtube';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isLinked && overview?.link) {
      if (platform === 'instagram') {
        captureHeapEvent({
          name: HEAP_EVENT_NAMES.CD_INSTAGRAM_CLICK_BASEBALL_CARD,
        });
      } else if (platform === 'youtube') {
        captureHeapEvent({
          name: HEAP_EVENT_NAMES.CD_YOUTUBE_CLICK_BASEBALL_CARD,
        });
      }
      window.open(overview.link, '_blank', 'noopener noreferrer');
    }
  };

  return (
    <SocialMediaButton
      id={`${platform}-${id}`}
      label={buttonLabel}
      disabled={!isLinked}
      showTooltip={!isLinked}
      aria-label={buttonAriaLabel}
      wrapperClassName="cn_atom_p-2"
      iconName={iconName}
      onClick={handleClick}
      tooltipContent={t('common:socialLinking.notLinked')}
    />
  );
};

export const SocialMediaSection: FC<SocialMediaSectionProps> = ({ socialMedia, id, showInstagram, showYoutube }) => {
  return (
    <FlexRow alignItems="center" gap="1" wrap="nowrap" data-testid="social-media-section">
      {showInstagram && <SocialPlatformButton platform="instagram" overview={socialMedia?.instagram} id={id} />}
      {showYoutube && <SocialPlatformButton platform="youtube" overview={socialMedia?.youtube} id={id} />}
    </FlexRow>
  );
};
